"use client";

import { useMemo, useState, useSyncExternalStore, useTransition } from "react";
import { submitFeedback } from "@/app/feedback/actions";
import Link from "next/link";
import { mountainFeedbackFromSearch, mountainFeedbackLabel, mountainFeedbackReturnPath, parseMountainFeedbackContext } from "@/lib/planner/feedback-context";
import { contentReportLabel, parseContentReportContext } from "@/lib/safety/content-report";
import {
  parsePisteMapIssueContext,
  pisteMapIssueLocationLabel,
  type PisteMapIssueContext,
} from "@/lib/maps/piste-map-issue";

const FEEDBACK_TYPES = [
  { value: "Bug report", label: "Bug report" },
  { value: "Feature suggestion", label: "Feature suggestion" },
  { value: "General feedback", label: "General feedback" },
  { value: "Something else", label: "Something else" },
  { value: "Safety or content report", label: "Safety or content report" },
];

const subscribeToStableBrowserContext = () => () => undefined;

function mountainFeedbackSnapshot(): string {
  const context = mountainFeedbackFromSearch(window.location.search);
  return context ? JSON.stringify(context) : "";
}

function contentReportSnapshot(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.get("context") !== "content-report") return "";
  return params.get("report") ?? "";
}

function readPisteMapContextRequested(): boolean {
  return (
    new URLSearchParams(window.location.search).get("context") === "piste-map"
  );
}

function pisteMapContextSnapshot(): string {
  if (!readPisteMapContextRequested()) return "";
  try {
    return sessionStorage.getItem("peakwave:piste-map-issue") ?? "";
  } catch {
    return "";
  }
}

export function FeedbackForm() {
  const rawMountainContext = useSyncExternalStore(subscribeToStableBrowserContext, mountainFeedbackSnapshot, () => "");
  const mountainContext = useMemo(() => parseMountainFeedbackContext(rawMountainContext), [rawMountainContext]);
  const [includeMountainContext, setIncludeMountainContext] = useState(true);
  const rawReportContext = useSyncExternalStore(subscribeToStableBrowserContext, contentReportSnapshot, () => "");
  const reportContext = useMemo(() => parseContentReportContext(rawReportContext), [rawReportContext]);
  const rawMapContext = useSyncExternalStore(
    subscribeToStableBrowserContext,
    pisteMapContextSnapshot,
    () => "",
  );
  const isPisteMapContextRequested = useSyncExternalStore(
    subscribeToStableBrowserContext,
    readPisteMapContextRequested,
    () => false,
  );
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<
    string | null
  >(null);
  const feedbackType = reportContext ? "Safety or content report" :
    selectedFeedbackType ?? (isPisteMapContextRequested ? "Bug report" : "General feedback");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [isPending, startTransition] = useTransition();
  const [includeMapContext, setIncludeMapContext] = useState(true);
  const mapContext = useMemo<PisteMapIssueContext | null>(
    () => parsePisteMapIssueContext(rawMapContext),
    [rawMapContext],
  );

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const formData = new FormData(form);
    if (mountainContext && includeMountainContext) formData.set("mountainContext", JSON.stringify(mountainContext));
    else formData.delete("mountainContext");
    formData.set("feedbackType", feedbackType);
    if (reportContext) formData.set("reportContext", JSON.stringify(reportContext));
    else formData.delete("reportContext");
    if (mapContext && includeMapContext) {
      formData.set("mapContext", JSON.stringify(mapContext));
    } else {
      formData.delete("mapContext");
    }

    startTransition(async () => {
      const result = await submitFeedback(formData);
      if (result.success) {
        setSuccess(true);
        try {
          sessionStorage.removeItem("peakwave:piste-map-issue");
        } catch {
          // Storage cleanup is best effort.
        }
      } else if (result.error) {
        setError(result.error);
      }
    });
  }

  return (
    <div className="max-w-[520px] mx-auto px-6 pb-12 md:pb-16">
      {mountainContext && <div className="mb-4 rounded-md border border-border-light bg-white p-4 text-sm text-navy">
        <p className="font-semibold">{mountainFeedbackLabel(mountainContext)}</p>
        <p className="mt-2 text-text-muted">Tell us what you tried, what looked wrong or confusing, and what would make this useful. You do not need to have completed a route.</p>
        <p className="mt-2 text-text-muted">For conditions feedback, include when you visited, the run or area and the approximate time.</p>
        <Link href={mountainFeedbackReturnPath(mountainContext)} className="mt-1 inline-flex min-h-11 items-center font-semibold text-teal underline underline-offset-4">Back to the map</Link>
      </div>}
      <div className="bg-white rounded-md p-6 md:p-8 border border-border-light">
        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-gold-light flex items-center justify-center text-2xl mx-auto mb-4">
              {"\u2713"}
            </div>
            <h2 className="font-serif text-2xl mb-2">{reportContext || feedbackType === "Safety or content report" ? "Report sent" : "Thanks for your feedback!"}</h2>
            <p className="text-sm text-text-muted leading-[1.7]">
              {reportContext || feedbackType === "Safety or content report" ? "Your report has been sent to PeakWave support for review." : "Your message has been received. Josh will read it personally."}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {reportContext && <div className="mb-5 rounded-sm border border-border-light bg-warm-bg p-3 text-sm text-navy">
              <h2 className="font-semibold">Report this {contentReportLabel(reportContext)}</h2>
              <p className="mt-1 text-xs leading-relaxed">We will include the item reference and your signed-in account ID so support can investigate. Message text is not attached automatically. Describe the concern below.</p>
            </div>}
            <div className="mb-4">
              <label htmlFor="feedback-type" className="block text-sm font-semibold text-navy mb-1.5">
                What type of feedback?
              </label>
              <select
                id="feedback-type"
                value={feedbackType}
                disabled={Boolean(reportContext)}
                onChange={(e) => setSelectedFeedbackType(e.target.value)}
                className="w-full min-h-[44px] px-4 py-3 border-[1.5px] border-border rounded-sm text-sm font-sans bg-warm-bg outline-none transition-all focus:border-gold"
              >
                {FEEDBACK_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="feedback-email" className="block text-sm font-semibold text-navy mb-1.5">
                Your email{" "}
                <span className="font-normal text-text-muted">(optional)</span>
              </label>
              <input
                id="feedback-email"
                name="email"
                type="email"
                maxLength={320}
                placeholder="In case we need to follow up"
                className="w-full min-h-[44px] px-4 py-3 border-[1.5px] border-border rounded-sm text-sm font-sans bg-warm-bg outline-none transition-all focus:border-gold placeholder:text-[#B0A99F]"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="feedback-message" className="block text-sm font-semibold text-navy mb-1.5">
                {reportContext || feedbackType === "Safety or content report" ? "What happened?" : "Tell us more"}
              </label>
              <textarea
                id="feedback-message"
                name="message"
                required
                maxLength={5000}
                rows={5}
                placeholder={reportContext || feedbackType === "Safety or content report" ? "Describe the content or behaviour you want us to review." : "What's working? What's not? What would you love to see?"}
                aria-describedby={error ? "feedback-error" : undefined}
                className="w-full px-4 py-3 border-[1.5px] border-border rounded-sm text-sm font-sans bg-warm-bg outline-none transition-all focus:border-gold resize-y min-h-[120px] placeholder:text-[#B0A99F]"
              />
            </div>

            {mapContext ? (
              <label className="mb-5 flex min-h-11 items-start gap-3 rounded-md border border-gold/30 bg-gold/10 px-3.5 py-3 text-[12px] leading-relaxed text-navy">
                <input
                  type="checkbox"
                  checked={includeMapContext}
                  onChange={(event) => setIncludeMapContext(event.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-[#C19A5B]"
                />
                <span>
                  <span className="block font-semibold">
                    Include the resort and map position
                  </span>
                  <span className="mt-0.5 block text-text-muted">
                    {pisteMapIssueLocationLabel(mapContext)}
                  </span>
                </span>
              </label>
            ) : null}

            {mountainContext && <label className="mb-5 flex min-h-11 items-start gap-3 rounded-md border border-border-light bg-warm-bg px-3.5 py-3 text-xs leading-relaxed text-navy">
              <input type="checkbox" checked={includeMountainContext} onChange={event => setIncludeMountainContext(event.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-teal" />
              <span><span className="block font-semibold">Include the resort, sport and feature</span><span className="mt-1 block text-text-muted">{mountainFeedbackLabel(mountainContext)}. Your route, GPS and crew positions are not attached.</span></span>
            </label>}

            {error && (
              <p id="feedback-error" role="alert" className="text-sm text-error mb-4">{error}</p>
            )}

            <button
              type="submit"
              disabled={isPending}
              className="w-full min-h-[44px] py-3.5 bg-gold text-white border-none rounded-sm text-[15px] font-semibold font-sans cursor-pointer transition-all hover:bg-gold-light disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isPending ? "Sending..." : reportContext || feedbackType === "Safety or content report" ? "Send report" : "Send Feedback \u2192"}
            </button>
          </form>
        )}
      </div>

      <p className="text-center text-[13px] text-text-muted mt-4">
        You can also email us directly at{" "}
        <a
          href="mailto:hello@peakwave.co"
          className="text-gold no-underline font-medium hover:underline"
        >
          hello@peakwave.co
        </a>
      </p>
    </div>
  );
}
